/**
 * El resumen del año encima de la tabla de conciliación: cuántos movimientos, cuántos completos
 * y cuántos pendientes; cuánto ha entrado, cuánto ha salido y el saldo.
 *
 * DEL 1 DE ENERO HASTA LA ÚLTIMA FECHA DE LA TABLA. Se suman los meses del año hasta el que se
 * está mirando, con las mismas filas y los mismos estados que la tabla (`filasDelMes`): si el
 * resumen contara de otra manera, los dos números nunca cuadrarían y nadie sabría cuál creer.
 * La fecha que se enseña es la del último movimiento de esos meses, no la de hoy: es la foto de
 * lo que de verdad hay subido.
 *
 * Cada mes es la misma consulta que la tabla, así que al pasar de mes en mes se reutilizan.
 */
import { useQueries } from '@tanstack/react-query';
import { api, queryKeys } from '../../api/client';
import { formatEurCents, formatShortDate } from '../../lib/format';
import { filasDelMes } from './reconciliation';
import styles from './YearSummary.module.css';

function euros(value: number): string {
  return value < 0 ? `-${formatEurCents(Math.abs(value))}` : formatEurCents(value);
}

export function YearSummary({ month }: { month: string }) {
  const year = month.slice(0, 4);
  const hasta = Number(month.slice(5, 7));
  const meses = Array.from({ length: hasta }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);

  const consultas = useQueries({
    queries: meses.map((key) => ({
      queryKey: queryKeys.ledgerMonth(key),
      queryFn: () => api.ledgerMonth(key),
    })),
  });
  const cargando = consultas.some((consulta) => consulta.isPending);

  let movimientos = 0;
  let completos = 0;
  let pendientes = 0;
  let ingresos = 0;
  let gastos = 0;
  let ultima: string | null = null;
  for (const consulta of consultas) {
    if (!consulta.data) continue;
    for (const fila of filasDelMes(consulta.data).filas) {
      movimientos += 1;
      if (fila.estado === 'completo') completos += 1;
      if (fila.estado === 'pendiente') pendientes += 1;
      // Lo previsto (un gasto que no ha llegado, una renta sin cobrar) no ha movido dinero.
      if (!fila.prevista && fila.importe !== null) {
        if (fila.importe > 0) ingresos += fila.importe;
        else gastos += -fila.importe;
      }
      if (fila.fecha && (!ultima || fila.fecha > ultima)) ultima = fila.fecha;
    }
  }
  const saldo = ingresos - gastos;
  const valor = (texto: string) => (cargando ? '…' : texto);

  return (
    <section className={styles.summary} aria-label={`Resumen de ${year}`}>
      <div className={styles.head}>
        <span className={styles.title}>Resumen {year}</span>
        <span className={styles.date}>
          {cargando ? 'Calculando…' : ultima ? `Hasta el ${formatShortDate(ultima)}` : 'Sin movimientos todavía'}
        </span>
      </div>

      <div className={styles.grid}>
        <div className={styles.group}>
          <div className={styles.item}>
            <span className={styles.label}>Movimientos del año</span>
            <span className={styles.value}>{valor(String(movimientos))}</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>
              <span className={`${styles.dot} ${styles.dotOk}`} aria-hidden="true" />
              Completos
            </span>
            <span className={styles.value}>{valor(String(completos))}</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>
              <span className={`${styles.dot} ${styles.dotPending}`} aria-hidden="true" />
              Pendientes
            </span>
            <span className={styles.value}>{valor(String(pendientes))}</span>
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.item}>
            <span className={styles.label}>Ingresos</span>
            <span className={`${styles.value} ${styles.income}`}>{valor(formatEurCents(ingresos))}</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>Gastos</span>
            <span className={styles.value}>{valor(gastos === 0 ? formatEurCents(0) : `-${formatEurCents(gastos)}`)}</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>Saldo</span>
            <span className={saldo < 0 ? `${styles.value} ${styles.negative}` : styles.value}>{valor(euros(saldo))}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
